import { computed, type Component } from 'vue'
import UiNavLink from '../../UiNavLink.vue'
import type { ButtonBaseProps } from '../types'
import { isExternalHref } from '../../../utils/isExternalHref'

export interface UseButtonBaseOptions {
    href?: string
    disabled?: boolean
}

export function useButtonBase(props: UseButtonBaseOptions) {
    /** Whether the button should render as a link */
    const isLink = computed(() => Boolean(props.href))

    /** Whether the link is external (http, mailto, tel) */
    const isExternal = computed(() => isExternalHref(props.href))

    /** The component to render (UiNavLink for links, 'button' otherwise) */
    const componentType = computed<Component | string>(() => {
        return isLink.value ? UiNavLink : 'button'
    })

    /** Whether hover effects should be disabled */
    const effectiveNoHover = computed(() => props.disabled)

    return {
        isLink,
        isExternal,
        componentType,
        effectiveNoHover,
    }
}

/**
 * Generate the bind attributes for the button/link component
 */
export function useButtonBindings(props: ButtonBaseProps & { href?: string; target?: string; rel?: string }) {
    const { isLink, isExternal } = useButtonBase(props)

    const bindings = computed(() => {
        if (isLink.value) {
            return {
                to: !isExternal.value ? props.href : undefined,
                href: props.href,
                external: isExternal.value,
                target: props.target,
                rel: props.rel,
            }
        }
        return {
            type: 'button' as const,
            disabled: props.disabled,
        }
    })

    return bindings
}
